/**
 * Yöneticinin kardeş adına günlük girişi (Hedef Gir) için saf yardımcılar.
 * Mobildeki components/groups-manage/memberDayHelpers.ts ve
 * amelAgendaHelpers.buildDayGoals ile aynı kurallar.
 */

const ACTIVITIES = ["okuma", "ezber", "yazma", "dinleme", "calisma"];

/** Hedef kimliği: ad + yapılış biçimi (biçimi olmayan eski hedef okuma sayılır). */
export function goalKey(label, activity) {
  const act = ACTIVITIES.includes(String(activity)) ? String(activity) : "okuma";
  return `${String(label ?? "").trim()}::${act}`;
}

export function isSameGoal(entry, label, activity) {
  if (!entry?.label) return false;
  return goalKey(entry.label, entry.activity) === goalKey(label, activity);
}

/** Hedefin geçerli olduğu haftanın günleri (0 = Pazar); boş liste her gün. */
export function appliesOn(weekDays, localDate) {
  if (!Array.isArray(weekDays) || weekDays.length === 0) return true;
  const [year, month, day] = String(localDate || "").split("-").map(Number);
  if (!year || !month || !day) return true;
  return weekDays.includes(new Date(Date.UTC(year, month - 1, day)).getUTCDay());
}

/** Telefonun saat dilimindeki bugün: "YYYY-MM-DD". */
export function todayString(now = new Date()) {
  const pad = (n) => String(n).padStart(2, "0");
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

/** Bugün dahil geriye doğru `count` gün, eskiden yeniye. */
export function lastDays(today, count) {
  const [year, month, day] = today.split("-").map(Number);
  const base = Date.UTC(year, month - 1, day);
  return Array.from({ length: count }, (_, i) =>
    new Date(base - (count - 1 - i) * 86400000).toISOString().slice(0, 10)
  );
}

const createdOnOrBefore = (goal, localDate) => {
  const created = goal?.createdInfo?.createdAt ? new Date(goal.createdInfo.createdAt) : null;
  if (!created || Number.isNaN(created.getTime())) return true;
  return todayString(created) <= localDate;
};

/**
 * Günün satırları: kayıt varsa o günkü satırlar, yoksa hedefler (değer 0;
 * o günden sonra eklenen hedef o günün hedefi değildir). Bugün kayıtlıysa
 * kayıtta olmayan hedefler de 0 değerle eklenir.
 */
function buildDayGoals(dayGoals, profileGoals, { isToday, localDate }) {
  const hasLog = dayGoals.length > 0;
  const source = hasLog
    ? dayGoals
    : profileGoals.filter((g) => createdOnOrBefore(g, localDate));
  const fromProfile = (entry) =>
    profileGoals.find((g) => isSameGoal(g, entry.label, entry.activity)) ||
    (entry.activity ? null : profileGoals.find((g) => g.label === entry.label)) ||
    null;

  const rows = source
    // Silinmiş hedefin boş satırı bugün gösterilmez; geçmişte o gün geçerliydi.
    .filter((g) => {
      if (!hasLog || !isToday) return true;
      const stillExists = profileGoals.some((p) => isSameGoal(p, g.label, g.activity));
      return stillExists || Number(g.value) > 0;
    })
    .map((g) => {
      const profile = hasLog ? fromProfile(g) : null;
      const settings = isToday && profile ? profile : g;
      const weekDays = Array.isArray(settings.weekDays)
        ? settings.weekDays
        : Array.isArray(profile?.weekDays)
          ? profile.weekDays
          : [];
      const work = settings.work ?? profile?.work;
      return {
        label: g.label,
        value: hasLog ? Number(g.value) || 0 : 0,
        goalValue: Number(settings.goalValue) || 0,
        isCustom: settings.isCustom ?? g.isCustom,
        unit: settings.unit ?? profile?.unit,
        activity: settings.activity ?? profile?.activity,
        weekDays,
        workId: work ? String(work) : null,
        workName: settings.workName ?? profile?.workName,
        enteredBy: hasLog ? g.enteredBy : undefined,
      };
    });

  if (hasLog && isToday) {
    for (const p of profileGoals) {
      if (rows.some((row) => isSameGoal(row, p.label, p.activity))) continue;
      rows.push({
        label: p.label,
        value: 0,
        goalValue: Number(p.goalValue) || 0,
        isCustom: p.isCustom,
        unit: p.unit,
        activity: p.activity,
        weekDays: Array.isArray(p.weekDays) ? p.weekDays : [],
        workId: p.work ? String(p.work) : null,
        workName: p.workName,
        enteredBy: undefined,
      });
    }
  }
  return rows;
}

/** Yöneticinin gördüğü günün grup satırları (kişisel hedefler hariç). */
export function memberDayRows(dayRows, groupGoals, localDate, today) {
  return buildDayGoals(dayRows, groupGoals, { isToday: localDate === today, localDate })
    .filter((g) => g.isCustom !== true)
    .filter((g) => appliesOn(g.weekDays, localDate));
}

/** Günün satırlarının ortalama tamamlanması (0..1), fazlası sayılmaz. */
export function dayProgress(rows) {
  const counted = rows.filter((r) => Number(r.goalValue) > 0);
  if (counted.length === 0) return 0;
  const sum = counted.reduce(
    (total, r) => total + Math.min(1, (Number(r.value) || 0) / Number(r.goalValue)),
    0
  );
  return sum / counted.length;
}

/**
 * Kaydedilecek satırlar: yalnız değeri değişenler. Günün tamamı gönderilseydi
 * kardeşin o arada girdiği değer eski değerle ezilir, "Yönetici girdi" olurdu.
 */
export function changedRows(draft, base) {
  return draft.filter((row) => {
    const before = base.find(
      (b) => b.label === row.label && (b.activity ?? "") === (row.activity ?? "")
    );
    return !before || Number(before.value) !== Number(row.value);
  });
}

/** Grup ve kardeş belli olmalı; yönetici kendi satırına buradan giremez. */
export function canEnterFor(groupId, memberId, meId) {
  return Boolean(groupId && memberId && String(memberId) !== String(meId ?? ""));
}

/** "Hepsini Tamamla": eksikler hedefe çıkar, fazlası korunur. */
export function completeAll(rows) {
  return rows.map((row) =>
    Number(row.value) < Number(row.goalValue)
      ? { ...row, value: Number(row.goalValue), enteredBy: undefined }
      : row
  );
}

/** "Hepsini Geri Al": bütün değerler 0. */
export function resetAll(rows) {
  return rows.map((row) =>
    Number(row.value) > 0 ? { ...row, value: 0, enteredBy: undefined } : row
  );
}

/** Sunucunun upsert-member-log-entry için beklediği satır. */
export function toPayload(row) {
  return {
    label: row.label,
    value: Number(row.value) || 0,
    goalValue: row.goalValue,
    isCustom: false,
    unit: row.unit,
    activity: row.activity,
    workId: row.workId ?? null,
    workName: row.workName,
  };
}
